import { useState } from "react";
import { Permission, PERMISSION_GROUPS, PERMISSION_LABELS, getPermissionGroup } from "../../utils/permissions";

interface PermissionsSelectorProps {
  selectedPermissions: Permission[];
  onChange: (permissions: Permission[]) => void;
  disabled?: boolean;
  compact?: boolean;
}

export function PermissionsSelector({
  selectedPermissions,
  onChange,
  disabled = false,
  compact = false,
}: PermissionsSelectorProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(PERMISSION_GROUPS.map((g) => g.id))
  );

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handlePermissionChange = (permission: Permission, checked: boolean) => {
    const newPermissions = checked
      ? [...selectedPermissions, permission]
      : selectedPermissions.filter((p) => p !== permission);
    onChange(newPermissions);
  };

  const handleSelectAllInGroup = (groupId: string, checked: boolean) => {
    const group = PERMISSION_GROUPS.find((g) => g.id === groupId);
    if (!group) return;

    const otherPermissions = selectedPermissions.filter(
      (p) => !group.permissions.includes(p)
    );
    const groupPermissions = checked ? group.permissions : [];
    onChange([...otherPermissions, ...groupPermissions]);
  };

  const isAllSelectedInGroup = (groupId: string): boolean => {
    const group = PERMISSION_GROUPS.find((g) => g.id === groupId);
    if (!group) return false;
    return group.permissions.every((p) => selectedPermissions.includes(p));
  };

  const isSomeSelectedInGroup = (groupId: string): boolean => {
    const group = PERMISSION_GROUPS.find((g) => g.id === groupId);
    if (!group) return false;
    return group.permissions.some((p) => selectedPermissions.includes(p));
  };

  return (
    <div className={`space-y-3 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      {PERMISSION_GROUPS.map((group) => {
        const allSelected = isAllSelectedInGroup(group.id);
        const someSelected = isSomeSelectedInGroup(group.id);

        return (
          <div
            key={group.id}
            className={`border rounded-lg overflow-hidden ${
              compact ? "border-gray-200" : "border-gray-300"
            }`}
          >
            <button
              type="button"
              onClick={() => toggleGroup(group.id)}
              disabled={disabled}
              className={`w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors ${
                disabled ? "cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = someSelected && !allSelected;
                    }
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectAllInGroup(group.id, e.target.checked);
                  }}
                  disabled={disabled}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="text-left">
                  <div className="font-semibold text-gray-900 text-sm">
                    {group.label}
                  </div>
                  {!compact && (
                    <div className="text-xs text-gray-500">
                      {group.description}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>
                  {selectedPermissions.filter((p) => group.permissions.includes(p))
                    .length}{" "}
                  / {group.permissions.length}
                </span>
                <svg
                  className={`w-4 h-4 transition-transform ${
                    expandedGroups.has(group.id) ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>

            {expandedGroups.has(group.id) && (
              <div className="border-t border-gray-200 divide-y divide-gray-100">
                {group.permissions.map((permission) => (
                  <label
                    key={permission}
                    className={`flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors ${
                      disabled ? "cursor-not-allowed" : "cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPermissions.includes(permission)}
                      onChange={(e) =>
                        handlePermissionChange(permission, e.target.checked)
                      }
                      disabled={disabled}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-700">
                        {PERMISSION_LABELS[permission] || permission}
                      </div>
                      {!compact && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {getPermissionGroup(permission)?.description}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}